import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getPaper, getReadingSession, updateLastPage, listNotes, deleteNote, getPaperLayout, getSessionRecap, getSessionProgress, getAnnotations, createAnnotation, updateAnnotation, deleteAnnotation, startAnnotationChat, logout } from '../services/api';
import PDFViewerCanvas from '../components/PDFViewerCanvas';
import ChatSection from '../components/ChatSection';
import RecapModal from '../components/RecapModal';
import ExportMenu from '../components/ExportMenu';
import GlossaryPanel from '../components/GlossaryPanel';
import '../styles/ReadingPage.css';

function TOCItem({ section, onSectionClick }) {
  return (
    <div className="toc-item" style={{ marginLeft: `${section.level * 15}px` }}>
      <button
        onClick={() => onSectionClick(section)}
        className="toc-link"
        disabled={!section.page}
        title={section.page ? `Go to page ${section.page}` : 'Page not found'}
      >
        {section.title}
        {section.page && <span className="toc-page"> (p. {section.page})</span>}
      </button>

      {section.children && section.children.length > 0 && (
        <div className="toc-children">
          {section.children.map((child, idx) => (
            <TOCItem
              key={idx}
              section={child}
              onSectionClick={onSectionClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReadingPage() {
  const [user, setUser] = useState(null);
  const [paper, setPaper] = useState(null);
  const [initialPage, setInitialPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [showTOC, setShowTOC] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedPdfText, setSelectedPdfText] = useState(null);
  // Last selection's line boxes in PDF points, kept with the text they came from.
  const selectionRectsRef = useRef(null);
  const [selectedPdfPage, setSelectedPdfPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [splitPercent, setSplitPercent] = useState(60);
  const [paperLayout, setPaperLayout] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [openAnnotation, setOpenAnnotation] = useState(null);
  const [annotationAnchor, setAnnotationAnchor] = useState(null);
  // Which marker's highlight is painted, so clicking it again clears it.
  const [litAnnotationId, setLitAnnotationId] = useState(null);
  // Viewport bounds of that highlight, so the bubble can open clear of the passage.
  const [highlightBounds, setHighlightBounds] = useState(null);
  const [testLocked, setTestLocked] = useState(false);
  const [openThreadRequest, setOpenThreadRequest] = useState(null);
  const [startingChat, setStartingChat] = useState(false);
  // Snapshot taken when the composer opens, so losing the selection keeps it open.
  const [composer, setComposer] = useState(null);
  const [composerQuestion, setComposerQuestion] = useState('');
  const [composerKind, setComposerKind] = useState('question');
  const [composerError, setComposerError] = useState('');
  const [savingAnnotation, setSavingAnnotation] = useState(false);
  const composerRef = useRef(null);
  // An account is one or the other: instructors always get instructor mode.
  const [mode] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.role === 'instructor'
        ? 'instructor' : 'student';
    } catch { return 'student'; }
  });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showStudents, setShowStudents] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [chatFontSize, setChatFontSize] = useState(() => {
    const saved = localStorage.getItem('paperbuddy_chat_font_size');
    return saved ? Number(saved) : 14;
  });

  const handleFontSizeChange = (delta) => {
    setChatFontSize(prev => {
      const next = Math.min(20, Math.max(11, prev + delta));
      localStorage.setItem('paperbuddy_chat_font_size', next);
      return next;
    });
  };
  const [recapAvailable, setRecapAvailable] = useState(false);
  const [recapChatIndex, setRecapChatIndex] = useState(null);
  const [recapSessionNumber, setRecapSessionNumber] = useState(1);
  const [showRecap, setShowRecap] = useState(false);
  const [recap, setRecap] = useState(null);
  const [recapError, setRecapError] = useState(null);
  const [loadingRecap, setLoadingRecap] = useState(false);
  const navigate = useNavigate();
  const { paperId } = useParams();
  const numericPaperId = paperId != null ? parseInt(paperId, 10) : NaN;
  const viewerRef = useRef(null);
  const lastPageSaveTimerRef = useRef(null);
  const userIdRef = useRef(null);
  const readingContentRef = useRef(null);
  const currentPageRef = useRef(null);
  const flatSectionsRef = useRef([]);

  const loadPaperAndSession = useCallback(async (userId, id) => {
    try {
      const paperData = await getPaper(id);
      setPaper(paperData.paper);

      const sessionData = await getReadingSession(userId, id);
      setInitialPage(sessionData.session.last_page || 1);

      // Durable resume: seed the per-mode localStorage snapshots from the DB BEFORE
      // ChatSection mounts (it's gated behind `loading`), so its existing restore
      // logic picks them up — making resume work on a fresh device / after a
      // localStorage clear. Only fill keys that are empty; never clobber newer
      // same-device state.
      try {
        const { progress = [] } = await getSessionProgress(userId, id);
        for (const row of progress) {
          // Skip finished/exited runs. Their snapshot lingers (exit only sets
          // status='completed'), and re-hydrating one would re-arm the test-lock or
          // re-open a completed cycle's modal. Only resume in-progress runs.
          if (row.status === 'completed') continue;
          const snap = row.snapshot || {};
          if (row.mode === 'test' && snap.testQuestions) {
            const key = `pb_test_${userId}_${id}_${row.session_number}`;
            if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(snap));
          } else if (row.mode === 'cycle' && snap.cyclePlan) {
            const key = `pb_cycle_${userId}_${id}_${row.session_number}`;
            if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(snap));
          } else if (row.mode === 'instructor' && Object.keys(snap).length) {
            const key = `pb_instructor_${userId}_${id}`;
            if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(snap));
          }
        }
      } catch (e) {
        console.warn('Failed to hydrate session progress:', e);
      }

      try {
        const notesData = await listNotes(userId, id);
        setNotes(notesData.notes || []);
      } catch (e) {
        console.warn('Failed to load notes:', e);
      }

      try {
        const layoutData = await getPaperLayout(id);
        setPaperLayout(layoutData.paragraphs || []);
      } catch (e) {
        console.warn('Failed to load paper layout:', e);
      }

      loadAnnotations(id);

    } catch (error) {
      console.error('Error loading data:', error);
      setPaper(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNoteCreated = useCallback((note) => {
    setNotes(prev => [...prev, note].sort((a, b) =>
      a.page_number - b.page_number || a.id - b.id,
    ));
  }, []);

  const handleDeleteNote = useCallback(async (noteId) => {
    try {
      await deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      console.error('Failed to delete note:', e);
      alert('Failed to delete note.');
    }
  }, []);

  const handleJumpToNote = useCallback((note) => {
    if (!viewerRef.current) return;
    if (note.paragraph_excerpt) {
      const ok = viewerRef.current.highlightParagraphOnPage(note.page_number, note.paragraph_excerpt);
      if (ok) return;
    }
    viewerRef.current.scrollToPage(note.page_number);
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/');
      return;
    }

    if (Number.isNaN(numericPaperId) || numericPaperId < 1) {
      navigate('/papers');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    userIdRef.current = parsedUser.id;
    setLoading(true);
    loadPaperAndSession(parsedUser.id, numericPaperId);
  }, [navigate, numericPaperId, loadPaperAndSession]);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMouseMove = (ev) => {
      if (!readingContentRef.current) return;
      const rect = readingContentRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(Math.max(pct, 20), 80));
    };

    const onMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleVisiblePageChange = useCallback((pageNum) => {
    const userId = userIdRef.current;
    if (!userId) return;
    if (Number.isNaN(numericPaperId) || numericPaperId < 1) return;
    if (!pageNum || pageNum < 1) return;

    const isFirstPage = currentPageRef.current === null;
    currentPageRef.current = pageNum;
    setCurrentPage(pageNum);

    if (lastPageSaveTimerRef.current) {
      window.clearTimeout(lastPageSaveTimerRef.current);
    }

    lastPageSaveTimerRef.current = window.setTimeout(async () => {
      try {
        await updateLastPage(userId, numericPaperId, pageNum);
      } catch (e) {
        console.error('Failed to update last page:', e);
      }
    }, 1200);

  }, [numericPaperId]);
  
  const applySections = useCallback((extractedSections) => {
    if (!extractedSections.length) return;
    setSections(extractedSections);
    const flat = [];
    const flatten = (secs) => {
      for (const s of secs) {
        if (s.page) flat.push(s);
        if (s.children?.length) flatten(s.children);
      }
    };
    flatten(extractedSections);
    flat.sort((a, b) => a.page - b.page);
    flatSectionsRef.current = flat;
    // Mark the section at the current page as already seen so no banner on load.
  }, []);

  const handleSectionsExtracted = useCallback((extractedSections) => {
    applySections(extractedSections);
  }, [applySections]);

  // Fallback: build sections from DB paragraph layout when the PDF has no embedded outline.
  useEffect(() => {
    if (sections.length > 0 || paperLayout.length === 0) return;

    const KNOWN_STANDALONE = new Set([
      'abstract', 'introduction', 'conclusion', 'conclusions', 'references',
      'acknowledgements', 'acknowledgments', 'related work', 'background',
      'discussion', 'results', 'methods', 'methodology', 'evaluation',
      'experiments', 'appendix', 'future work', 'summary',
    ]);
    // First words that signal a list item or sentence, not a section title
    const SKIP_WORDS = new Set([
      'the', 'a', 'an', 'one', 'of', 'in', 'at', 'for', 'by', 'to', 'it',
      'this', 'that', 'when', 'while', 'if', 'as', 'with', 'is', 'are',
      'was', 'were', 'all', 'each', 'every', 'after', 'before', 'upon',
      'once', 'during', 'periodically', 'finally', 'however', 'thus',
      'january', 'february', 'march', 'april', 'may', 'june', 'july',
      'august', 'september', 'october', 'november', 'december',
    ]);

    const lastPage = paper?.total_pages || 0;

    const isRealSection = (p) => {
      const raw = (p.text || '').trim();
      if (!raw) return false;

      // Known standalone headings (Abstract, References, etc.)
      if (p.kind === 'heading' && KNOWN_STANDALONE.has(raw.toLowerCase())) return true;

      // Unnumbered heading: keep if reasonable length (paper title, named sections)
      // Exclude very short labels (Master, Input, Worker) and very long body text
      if (p.kind === 'heading' && !/^\d/.test(raw)) {
        return raw.length >= 12 && raw.length <= 80;
      }

      // Parse section number prefix
      const m = raw.match(/^(\d+)(?:\.\d+)*\.?\s+(\w[\w-]*)/);
      if (!m) return false;

      const num = parseInt(m[1], 10);
      const firstWord = m[2].toLowerCase();

      if (num >= 20) return false;                       // journal page numbers
      if (SKIP_WORDS.has(firstWord)) return false;       // list items
      if (/^\d+\.?\s+\w[\w-]*,/.test(raw)) return false; // reference entries ("3. Author, A.")
      // On the last page, skip numbered entries with a trailing period (bibliography refs)
      if (lastPage > 0 && p.page >= lastPage && /^\d+\.\s/.test(raw)) return false;

      return true;
    };

    // Words that typically open a body sentence, not a section title.
    const BODY_STARTERS = new Set([
      'the','a','an','our','we','i','it','its','they','their','my','your',
      'this','these','that','those','there','here',
      'in','for','when','after','before','if','as','while','although',
      'since','because','prior','during','upon','with','at','from','to',
      'by','on','about','over','under','between','throughout',
      'however','moreover','furthermore','thus','therefore','hence',
      'additionally','consequently','nevertheless',
      'consider','note','recall','assume','suppose','let','given',
      'many','most','each','all','some','any','several','various',
      'both','much','few','no','more','less','one','two','three',
      'such','another','other','similar','different',
      'finally','first','second','third','recently','currently',
      'previously','typically','usually','generally','often','also',
    ]);

    const extractTitle = (raw) => {
      const numMatch = raw.match(/^(\d+(?:\.\d+)*\.?\s+)/);
      const prefix = numMatch ? numMatch[1] : '';
      const words = raw.slice(prefix.length).trim().split(/\s+/);
      const kept = [];
      for (const w of words) {
        if (kept.length >= 4) break;
        const lower = w.toLowerCase().replace(/[^a-z]/g, '');
        if (BODY_STARTERS.has(lower)) break;
        if (/^[a-z]/.test(w)) break; // lowercase → body text started
        kept.push(w);
      }
      return (prefix + (kept.length ? kept.join(' ') : words[0])).trim();
    };

    const derived = paperLayout
      .filter(isRealSection)
      .map(p => {
        const raw = (p.text || '').trim();
        const title = extractTitle(raw);
        const rects = p.line_bboxes?.length ? p.line_bboxes : (p.bbox ? [p.bbox] : null);
        return { title, page: p.page, level: 0, children: [], rects };
      });

    // Deduplicate by title
    const seen = new Set();
    const unique = derived.filter(s => {
      if (seen.has(s.title)) return false;
      seen.add(s.title);
      return true;
    });

    applySections(unique);
  }, [paperLayout, sections.length, applySections, paper?.total_pages]);

  const loadAnnotations = useCallback(async (paperId) => {
    try {
      const data = await getAnnotations(paperId);
      // Only anchored ones can be drawn.
      setAnnotations((data.annotations || []).filter(a => a.resolved));
    } catch (e) {
      console.warn('Failed to load annotations:', e);
    }
  }, []);

  const closeComposer = useCallback(() => {
    composerRef.current = null;
    setComposer(null);
  }, []);

  const chatAboutAnnotation = useCallback(async (a) => {
    setStartingChat(true);
    try {
      const started = await startAnnotationChat(a.id);
      // The highlight stays lit while they discuss the passage.
      setOpenAnnotation(null);
      setAnnotationAnchor(null);
      setOpenThreadRequest({
        chatIndex: started.chat_index,
        annotationId: a.id,
        annotation: started.annotation || a,
        resumed: !!started.resumed,
        nonce: Date.now(),
      });
    } catch (e) {
      console.error('Could not start a conversation about this marker:', e);
    } finally {
      setStartingChat(false);
    }
  }, []);

  const clearAnnotationHighlight = useCallback(() => {
    viewerRef.current?.clearAnnotationHighlight?.();
    setLitAnnotationId(null);
    setHighlightBounds(null);
  }, []);

  const handleAnnotationClick = useCallback((a, rect) => {
    // Clicking the lit marker again turns it off.
    if (a.id === litAnnotationId) {
      setOpenAnnotation(null);
      setAnnotationAnchor(null);
      clearAnnotationHighlight();
      return;
    }
    const lit = a.rects && a.rects.length;
    if (lit) {
      viewerRef.current?.paintAnnotationHighlight?.([{ page: a.page, rects: a.rects }]);
      setLitAnnotationId(a.id);
    } else {
      clearAnnotationHighlight();
    }
    // The viewer paints on a later tick, so open the bubble once its bounds
    // exist — otherwise placement falls back to the marker and covers the passage.
    setTimeout(() => {
      setHighlightBounds(lit ? (viewerRef.current?.getAnnotationHighlightBounds?.() || null) : null);
      setAnnotationAnchor(rect || null);
      setOpenAnnotation(a);
    }, 0);
  }, [mode, litAnnotationId, clearAnnotationHighlight]);

  // Opened clear of the passage itself, not just the marker: right of it, else
  // left of it, else below it.
  const BUBBLE_W = 320;
  const GAP = 12;
  const annotationBubble = (() => {
    const r = annotationAnchor;
    if (!r) return { left: 24, top: 90, place: 'right' };
    const hl = highlightBounds;
    const blockLeft = hl ? Math.min(hl.left, r.left) : r.left;
    const blockRight = hl ? Math.max(hl.right, r.right) : r.right;
    const top = Math.min(Math.max(GAP, r.top - 8), window.innerHeight - 160);

    // Stay inside the PDF pane: the chat panel beside it is its own reading surface.
    const pane = document.querySelector('.pdf-panel')?.getBoundingClientRect();
    const minX = pane ? pane.left + GAP : GAP;
    const maxX = (pane ? pane.right : window.innerWidth) - BUBBLE_W - GAP;

    if (blockRight + GAP <= maxX) {
      return { left: blockRight + GAP, top, place: 'right' };
    }
    if (blockLeft - BUBBLE_W - GAP >= minX) {
      return { left: blockLeft - BUBBLE_W - GAP, top, place: 'left' };
    }
    return {
      left: Math.min(Math.max(minX, blockLeft), Math.max(minX, maxX)),
      top: Math.min((hl ? hl.bottom : r.bottom) + GAP, window.innerHeight - 160),
      place: 'below',
    };
  })();

  const closeAnnotation = useCallback(() => {
    setOpenAnnotation(null);
    setAnnotationAnchor(null);
    clearAnnotationHighlight();
  }, [clearAnnotationHighlight]);

  // Follows the marker on scroll; painting a highlight scrolls the page itself.
  useEffect(() => {
    if (!openAnnotation) return;
    let frame = 0;
    const reposition = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const el = document.querySelector(
          `.pb-annotation-marker[data-annotation-id="${openAnnotation.id}"]`);
        if (!el) return closeAnnotation();
        setAnnotationAnchor(el.getBoundingClientRect());
        setHighlightBounds(viewerRef.current?.getAnnotationHighlightBounds?.() || null);
      });
    };
    const onClickAway = (e) => {
      if (e.target?.closest?.('.pb-annotation-bubble, .pb-annotation-marker')) return;
      closeAnnotation();
    };
    document.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [openAnnotation, closeAnnotation]);

  const openComposer = useCallback(() => {
    // Fall back to the live selection if state was already cleared.
    const text = selectedPdfText || window.getSelection()?.toString().trim();
    if (!text) return;
    const cached = selectionRectsRef.current;
    const snapshot = {
      id: null,
      anchorText: text,
      page: selectedPdfPage || currentPage || 1,
      rects: cached?.text === text ? cached.rects : [],
    };
    composerRef.current = snapshot;
    setComposer(snapshot);
    setComposerQuestion('');
    setComposerKind('question');
    setComposerError('');
  }, [selectedPdfText, selectedPdfPage, currentPage]);

  const openEditComposer = useCallback((annotation) => {
    const snapshot = {
      id: annotation.id,
      anchorText: annotation.anchor_text,
      page: annotation.page,
      rects: annotation.rects || [],
    };
    composerRef.current = snapshot;
    setComposer(snapshot);
    setComposerQuestion(annotation.body || '');
    setComposerKind(annotation.kind);
    setComposerError('');
  }, []);

  const saveAnnotation = useCallback(async () => {
    const body = composerQuestion.trim();
    if (!composer) return;
    if (composerKind === 'question' && !body) return;
    setSavingAnnotation(true);
    setComposerError('');
    try {
      if (composer.id) {
        await updateAnnotation(composer.id, { body, kind: composerKind });
        if (openAnnotation?.id === composer.id) closeAnnotation();
      } else {
        const created = await createAnnotation(
          paper.id, composer.page, composer.anchorText, body, composerKind, composer.rects);
        if (!created.resolved) {
          setComposerError('Saved, but the passage could not be located, so no marker will show.');
        }
      }
      await loadAnnotations(paper.id);
      closeComposer();
      setSelectedPdfText(null);
      setSelectedPdfPage(null);
      viewerRef.current?.clearSelectionHighlights();
    } catch (e) {
      setComposerError(e?.response?.data?.error || 'Could not save the question.');
    } finally {
      setSavingAnnotation(false);
    }
  }, [composer, composerQuestion, composerKind, paper, loadAnnotations, openAnnotation, closeAnnotation]);

  const removeAnnotation = useCallback(async (annotationId) => {
    try {
      await deleteAnnotation(annotationId);
      closeAnnotation();
      await loadAnnotations(paper.id);
    } catch (e) {
      console.warn('Failed to delete annotation:', e);
    }
  }, [paper, loadAnnotations, closeAnnotation]);

  const handleTextSelected = useCallback((text, pageNum, rects) => {
    setSelectedPdfText(text);
    if (pageNum != null) setSelectedPdfPage(pageNum);
    selectionRectsRef.current = rects?.length ? { text, rects } : null;
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedPdfText(null);
    setSelectedPdfPage(null);
    selectionRectsRef.current = null;
    if (viewerRef.current) {
      viewerRef.current.clearSelectionHighlights();
    }
  }, []);

  // Clear chip + overlays when the browser selection goes empty,
  // but not when focus moves into the chat (that's intentional — keep chip visible).
  useEffect(() => {
    if (!selectedPdfText) return;
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (sel && sel.toString().trim() !== '') return;
      const chatEl = document.querySelector('.chat-section');
      if (chatEl && chatEl.contains(document.activeElement)) return;
      // The composer holds its own snapshot; clearing here would hide the prompt
      // out from under a click that has not landed yet.
      if (composerRef.current) return;
      setSelectedPdfText(null);
      setSelectedPdfPage(null);
      viewerRef.current?.clearSelectionHighlights();
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [selectedPdfText]);

  const handleZoomRequest = useCallback((pageNum, figureHint) => {
    viewerRef.current?.zoomToFigure(pageNum, figureHint);
  }, []);

  const handleHighlightRequest = useCallback((pageNum, searchTextOrIndex) => {
    if (!viewerRef.current) return;
    if (searchTextOrIndex == null) {
      viewerRef.current.scrollToPage(pageNum);
      return;
    }
    const success = typeof searchTextOrIndex === 'number'
      ? viewerRef.current.highlightParagraphByIndex(pageNum, searchTextOrIndex)
      : viewerRef.current.highlightParagraphOnPage(pageNum, searchTextOrIndex);
    if (!success) {
      console.warn(`Failed to highlight on page ${pageNum}:`, searchTextOrIndex);
    }
  }, []);

  // Called when the server resolved a highlight action to exact bounding boxes.
  // `options.persistent` marks plan highlights (instructor cycle/test preview +
  // the student's active-item highlights) so the viewer paints them in a layer
  // that clicks and scrolling never dismiss — only a new highlight set replaces.
  const handleHighlightResolved = useCallback((pageNum, rects, pages, options) => {
    if (!viewerRef.current) return;
    const persistent = !!options?.persistent;
    // A section can span multiple pages, and a response can carry several
    // highlight spans — draw every group together when present. An empty
    // groups array is an explicit clear (e.g. the instructor's cycle preview
    // after the last highlight was removed).
    if (Array.isArray(pages) && viewerRef.current.highlightFromLayoutPages) {
      viewerRef.current.highlightFromLayoutPages(pages, persistent);
    } else {
      viewerRef.current.highlightFromLayout(pageNum, rects, persistent);
    }
  }, []);

  const handleSectionClick = (section) => {
    if (!viewerRef.current || !section.page) return;
    if (section.rects?.length) {
      // Precise scroll + highlight using stored bounding boxes
      viewerRef.current.highlightFromLayout(section.page, section.rects);
    } else {
      viewerRef.current.scrollToPage(section.page);
    }
  };

  const handleRecapContextChange = useCallback((ctx) => {
    const available = typeof ctx === 'boolean' ? ctx : !!ctx?.available;
    setRecapAvailable(available);
    if (ctx && typeof ctx === 'object') {
      if (ctx.chatIndex != null) setRecapChatIndex(ctx.chatIndex);
      if (ctx.sessionNumber != null) setRecapSessionNumber(ctx.sessionNumber);
    }
  }, []);

  const handleOpenRecap = async () => {
    if (!user || !paper) return;
    setShowRecap(true);
    setRecap(null);
    setRecapError(null);
    setLoadingRecap(true);
    try {
      const data = await getSessionRecap(
        user.id,
        paper.id,
        recapSessionNumber,
        recapChatIndex,
      );
      if (data?.error) throw new Error(data.error);
      const summary = (data?.summary || '').trim();
      if (summary.startsWith('Recap unavailable') || summary.startsWith('Error:')) {
        throw new Error(summary);
      }
      if (!summary && !(data?.flashcards?.length)) {
        throw new Error('No recap content was returned. Try chatting a bit more first.');
      }
      setRecap(data);
    } catch (e) {
      console.error('Failed to fetch recap:', e);
      setRecap(null);
      const isNetwork = !e.response && (e.code === 'ERR_NETWORK' || e.message === 'Network Error');
      setRecapError(
        isNetwork
          ? 'Could not reach the server. Make sure Docker is running (docker compose up) and try again.'
          : e.response?.data?.error || e.message || 'Something went wrong generating the recap.',
      );
    } finally {
      setLoadingRecap(false);
    }
  };

  const handleCloseRecap = () => setShowRecap(false);

  const handleRecapInvalidate = useCallback(() => {
    setRecap(null);
    setRecapError(null);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading paper...</p>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="error-container">
        <p>Failed to load paper. Please try again.</p>
        <button type="button" onClick={() => navigate('/papers')}>Back to papers</button>
      </div>
    );
  }

return (
  <div className="reading-container">
    {showRecap && (
      <RecapModal
        recap={recap}
        paper={paper}
        loading={loadingRecap}
        error={recapError}
        onClose={handleCloseRecap}
      />
    )}
    {showGlossary && (
      <GlossaryPanel
        paper={paper}
        user={user}
        currentPage={currentPage}
        selectedText={selectedPdfText}
        onClose={() => setShowGlossary(false)}
      />
    )}
    {openAnnotation && (
      <div
        className={`pb-annotation-bubble pb-annotation-bubble--${annotationBubble.place}`}
        style={{ left: annotationBubble.left, top: annotationBubble.top }}
      >
        <div className="pb-annotation-bubble__head">
          <span className="pb-annotation-card__badge">
            {openAnnotation.kind === 'emphasis'
              ? 'Flagged as important'
              : (openAnnotation.author_name
                  ? `Question from ${openAnnotation.author_name}`
                  : 'Question from your instructor')}
          </span>
          <button
            type="button"
            className="pb-annotation-card__close"
            onClick={closeAnnotation}
          >
            ×
          </button>
        </div>
        <p className="pb-annotation-bubble__body">
          {openAnnotation.body
            || (openAnnotation.kind === 'emphasis'
                ? 'Your instructor marked this passage as important.'
                : 'Your instructor marked this passage.')}
        </p>
        {testLocked && (
          <p className="pb-annotation-bubble__locked">
            Finish or exit your test to discuss this passage.
          </p>
        )}
        <div className="pb-annotation-card__actions">
          {/* Students only: the thread is reserved in their Q&A session, which
              instructor mode does not read. Instructors have their own chat. */}
          {mode !== 'instructor' && !testLocked && (
            <button
              type="button"
              className="pb-annotation-btn"
              disabled={startingChat}
              onClick={() => chatAboutAnnotation(openAnnotation)}
            >
              {startingChat ? 'Opening…' : 'Chat about this'}
            </button>
          )}
          {mode === 'instructor' && (
            <>
              <button
                type="button"
                className="pb-annotation-btn"
                onClick={() => openEditComposer(openAnnotation)}
              >
                Edit
              </button>
              <button
                type="button"
                className="pb-annotation-btn pb-annotation-btn--danger"
                onClick={() => removeAnnotation(openAnnotation.id)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    )}

    {mode === 'instructor' && selectedPdfText && !composer && (
      <div className="pb-annotation-prompt">
        <span className="pb-annotation-prompt__text">
          "{selectedPdfText.length > 60 ? `${selectedPdfText.slice(0, 60)}…` : selectedPdfText}"
        </span>
        <button
          type="button"
          className="pb-annotation-btn"
          onMouseDown={(e) => { e.preventDefault(); openComposer(); }}
        >
          Add annotation
        </button>
      </div>
    )}

    {composer && (
      <div className="pb-annotation-overlay" onClick={() => closeComposer()}>
        <div className="pb-annotation-card" onClick={(e) => e.stopPropagation()}>
          <div className="pb-annotation-card__head">
            <span className="pb-annotation-card__badge">
              {composer.id
                ? (composerKind === 'emphasis' ? 'Edit marker' : 'Edit question')
                : (composerKind === 'emphasis' ? 'Mark as important' : 'New question')} · p.{composer.page}
            </span>
            <button
              type="button"
              className="pb-annotation-card__close"
              onClick={() => closeComposer()}
            >
              ×
            </button>
          </div>
          <blockquote className="pb-annotation-card__anchor">
            "{composer.anchorText}"
          </blockquote>
          <div className="pb-annotation-kinds">
            <button
              type="button"
              className={`pb-annotation-kind${composerKind === 'question' ? ' is-active' : ''}`}
              onClick={() => setComposerKind('question')}
            >
              ? Ask a question
            </button>
            <button
              type="button"
              className={`pb-annotation-kind${composerKind === 'emphasis' ? ' is-active' : ''}`}
              onClick={() => setComposerKind('emphasis')}
            >
              ! Mark as important
            </button>
          </div>
          <textarea
            className="pb-annotation-input"
            autoFocus
            rows={3}
            placeholder={composerKind === 'emphasis'
              ? 'Optional: why does this passage matter?'
              : 'What should students think about here?'}
            value={composerQuestion}
            onChange={(e) => setComposerQuestion(e.target.value)}
          />
          {composerError && <p className="pb-annotation-error">{composerError}</p>}
          <div className="pb-annotation-card__actions">
            <button
              type="button"
              className="pb-annotation-btn pb-annotation-btn--ghost"
              onClick={() => closeComposer()}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pb-annotation-btn"
              disabled={savingAnnotation || (composerKind === 'question' && !composerQuestion.trim())}
              onClick={saveAnnotation}
            >
              {savingAnnotation
                ? 'Saving…'
                : (composer.id
                    ? 'Save changes'
                    : (composerKind === 'emphasis' ? 'Add marker' : 'Add question'))}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Header */}
    <header className="reading-header">
      <div className="header-left">
        <Link to="/papers" className="reading-back-link">← Papers</Link>
        <h1>PaperBuddy</h1>
        <span className="paper-title">{paper.name}</span>
      </div>
      <div className="header-right">
        {mode === 'student' && (
          <button
            type="button"
            onClick={handleOpenRecap}
            disabled={!recapAvailable}
            className={`recap-toggle-btn${showRecap ? ' active' : ''}`}
            title={recapAvailable
              ? 'Generate session recap and flashcards'
              : 'Ask a question in Q&A mode first to generate a recap'}
          >
            Recap
          </button>
        )}
        {sections.length > 0 && (
          <button
            type="button"
            onClick={() => { setShowTOC(s => !s); if (!showTOC) setShowNotes(false); }}
            className={`toc-toggle-btn${showTOC ? ' active' : ''}`}
            title="Table of contents"
          >
            Contents
          </button>
        )}
        {mode === 'instructor' ? (
          <>
            <button
              type="button"
              onClick={() => { setShowStudents(s => !s); setShowAnalytics(false); }}
              className={`analytics-toggle-btn${showStudents ? ' active' : ''}`}
              title="Consolidated overview of everything each student has done"
            >
              Students
            </button>
            <button
              type="button"
              onClick={() => { setShowAnalytics(s => !s); setShowStudents(false); }}
              className={`analytics-toggle-btn${showAnalytics ? ' active' : ''}`}
              title="Class analytics"
            >
              Analytics
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setShowGlossary(true)}
              className="glossary-toggle-btn"
              title="Glossary"
            >
              Glossary
            </button>
            <button
              type="button"
              onClick={() => { setShowNotes(s => !s); if (!showNotes) setShowTOC(false); }}
              className={`notes-toggle-btn ${showNotes ? 'active' : ''}`}
              title="My notes"
            >
              Notes {notes.length > 0 && <span className="notes-count">{notes.length}</span>}
            </button>
          </>
        )}
        <div className="font-size-controls">
          <button className="font-size-btn" onClick={() => handleFontSizeChange(-1)} title="Decrease font size" disabled={chatFontSize <= 11}>A−</button>
          <button className="font-size-btn" onClick={() => handleFontSizeChange(1)} title="Increase font size" disabled={chatFontSize >= 20}>A+</button>
        </div>
        <span className="user-name">{user?.name}</span>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>
    </header>

      {/* Reading progress bar */}
      {paper.total_pages > 0 && (
        <div className="reading-progress-bar">
          <div
            className="reading-progress-fill"
            style={{ width: `${((currentPage || 0) / paper.total_pages) * 100}%` }}
          />
        </div>
      )}

      {/* Main Content - Split Screen */}
      <div className="reading-content" ref={readingContentRef}>
        {/* TOC Sidebar */}
        {showTOC && sections.length > 0 && (
          <aside className="toc-sidebar">
            <h3>Table of Contents</h3>
            <div className="toc-list">
              {sections.map((section, idx) => (
                <TOCItem
                  key={idx}
                  section={section}
                  onSectionClick={handleSectionClick}
                />
              ))}
            </div>
          </aside>
        )}

        {/* Notes Sidebar */}
        {showNotes && (
          <aside className="notes-sidebar">
            <div className="notes-sidebar-header">
              <h3>My Notes</h3>
              <ExportMenu notes={notes} paper={paper} disabled={notes.length === 0} />
            </div>
            {notes.length === 0 ? (
              <p className="notes-empty">
                No notes yet. Select text in the PDF, then click 📝 in the chat panel to add a note.
              </p>
            ) : (
              <div className="notes-list">
                {notes.map(note => (
                  <div className="note-card" key={note.id}>
                    <div className="note-card-header">
                      <button
                        type="button"
                        className="note-page-btn"
                        onClick={() => handleJumpToNote(note)}
                        title="Jump to this paragraph"
                      >
                        p. {note.page_number}
                      </button>
                      <button
                        type="button"
                        className="note-delete-btn"
                        onClick={() => handleDeleteNote(note.id)}
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        ×
                      </button>
                    </div>
                    {note.paragraph_excerpt && (
                      <div className="note-excerpt">
                        "{note.paragraph_excerpt.length > 100
                          ? note.paragraph_excerpt.slice(0, 100) + '…'
                          : note.paragraph_excerpt}"
                      </div>
                    )}
                    <div className="note-body">{note.body}</div>
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}

        <div className="pdf-panel" style={{ flex: `0 0 ${splitPercent}%` }}>
          <PDFViewerCanvas
            key={paper.id}
            ref={viewerRef}
            pdfUrl={paper.pdf_url || paper.url}
            initialPage={initialPage}
            onVisiblePageChange={handleVisiblePageChange}
            onSectionsExtracted={handleSectionsExtracted}
            onTextSelected={handleTextSelected}
            paragraphLayout={paperLayout}
            annotations={annotations}
            onAnnotationClick={handleAnnotationClick}
          />
        </div>

        <div className="split-divider" onMouseDown={handleDividerMouseDown} />

        <div className="chat-panel">
          <ChatSection
            user={user}
            paper={paper}
            currentSession={1}
            currentPage={currentPage}
            openThreadRequest={openThreadRequest}
            onTestLockChange={setTestLocked}
            onHighlightRequest={handleHighlightRequest}
            onHighlightResolved={handleHighlightResolved}
            onZoomRequest={handleZoomRequest}
            selectedPdfText={selectedPdfText}
            selectedPdfPage={selectedPdfPage}
            onClearSelection={handleClearSelection}
            onNoteCreated={handleNoteCreated}
            mode={mode}
            showAnalytics={showAnalytics}
            setShowAnalytics={setShowAnalytics}
            showStudents={showStudents}
            setShowStudents={setShowStudents}
            onRecapAvailableChange={handleRecapContextChange}
            onRecapInvalidate={handleRecapInvalidate}
            chatFontSize={chatFontSize}
            tocSections={sections}
          />
        </div>
      </div>
  </div>
);
}
export default ReadingPage;